import { Card, CardActions, CardContent, Typography } from '@mui/material';
import {
    FormInputGroup,
    FormProvider,
    FormSubmitButton,
    FormText,
} from 'components/form';
import * as yup from 'yup';
import {
    FtpConnectionDetails,
    SupplierInfo,
    SupportedBackOffices,
} from 'types';
import { useEffect, useMemo, useState } from 'react';
import {
    getSupplierBackoffice,
    saveBackoffice,
} from 'store/services/third-party-backoffice';
import { activateSnackbar } from 'store/features/ui';
import { useDispatch } from 'react-redux';
import { FieldValues } from 'react-hook-form';
import { StoredUser } from 'store/features/authentication';

export interface FtpBackofficeFormProps {
    backofficeName: SupportedBackOffices;
    supplierInfo: SupplierInfo;
    supplierId: string;
    user: StoredUser;
}

const backofficeSchema = yup.object<{
    [k in keyof FtpConnectionDetails]: yup.AnySchema;
}>({
    url: yup.string().required(),
    ftp_username: yup.string().required(),
    ftp_password: yup.string().required(),
});

/** Third-party backoffice configuration page for backoffice platforms other than CarGas and ADDs. Most/all of the fields on this form are intentionally not editable by the user. */
export const FtpBackofficeForm = ({
    supplierId,
    supplierInfo,
    backofficeName,
    user,
}: FtpBackofficeFormProps) => {
    const [ftpInfo, setFtpInfo] = useState<FtpConnectionDetails>();

    const defaultValues = useMemo(() => {
        return {
            url: 'sftp.tankutility.com',
            ftp_username: supplierInfo?.ftp_username,
            ftp_password: supplierInfo?.ftp_password,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ftpInfo]);

    const [isLoading, setIsLoading] = useState(true);
    const dispatch = useDispatch();

    useEffect(() => {
        getSupplierBackoffice(supplierId, backofficeName).then(
            (result: FtpConnectionDetails) => {
                setFtpInfo(result);
                setIsLoading(false);
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supplierId]);

    const onSave = (ftpDetails: FtpConnectionDetails) => {
        saveBackoffice(
            supplierId,
            supplierInfo,
            user,
            backofficeName,
            ftpDetails
        )
            .then(() => {
                dispatch(
                    activateSnackbar({
                        message: 'Saved Successfully',
                        severity: 'success',
                    })
                );
            })
            .catch((error) => {
                dispatch(
                    activateSnackbar({
                        message: JSON.stringify(error),
                        severity: 'error',
                    })
                );
            });
    };

    const onError = (error: FieldValues) => {
        const messages = Object.keys(error).map((key) => error[key]?.message);
        dispatch(
            activateSnackbar({
                message: JSON.stringify(messages),
                severity: 'error',
            })
        );
    };

    return (
        <div data-testid="FtpBackofficeForm">
            {!isLoading ? (
                <Card>
                    <CardContent>
                        <FormProvider
                            defaultValues={defaultValues}
                            validationSchema={backofficeSchema}
                        >
                            <Typography variant="h6">
                                FTP Connection Details for {backofficeName}
                            </Typography>
                            <FormInputGroup>
                                <FormText
                                    disabled
                                    field="url"
                                    label="Remote URL"
                                />
                            </FormInputGroup>
                            <FormInputGroup>
                                <FormText
                                    disabled
                                    field="ftp_username"
                                    label="FTP Username"
                                />
                            </FormInputGroup>
                            <FormInputGroup>
                                <FormText
                                    disabled
                                    field="ftp_password"
                                    label="FTP Password"
                                />
                            </FormInputGroup>
                            <CardActions
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <FormSubmitButton
                                    onValidHandler={(
                                        input: FtpConnectionDetails
                                    ) => onSave(input)}
                                    onErrorHandler={onError}
                                    buttonLabel="Save"
                                />
                            </CardActions>
                        </FormProvider>
                    </CardContent>
                </Card>
            ) : null}
        </div>
    );
};
